import React, { useEffect, useMemo, useState } from "react";
import { TeamScorecard } from "../components/TeamScorecard";
import { can } from "../lib/permissions";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  TrendingUp, Users, CalendarClock, AlertOctagon, Home,
  CheckCircle2, PhoneCall, Activity, ArrowUpRight, Trophy, Crown, Medal, UserPlus,
  Layers, Target, Save, Pencil, ClipboardList, ChevronRight,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { toast } from "sonner";

// KPI order per user request:
// Row 1: Worked Leads · Today's Calls · Pending Calls · Upcoming Calls
// Row 2: Total Leads · New Leads (attractive) · Site Visits (combo Fixed+Done) · Booked
//
// `theme` drives the per-card premium palette (subtle 95% white gradients + soft
// radial glow behind the icon). Only the *visual identity* differs per card;
// layout, spacing, typography, and dimensions remain identical.
const ROW1_KPIS = [
  // Worked Leads is rendered separately via a split-metric card (Pool + Follow Up).
  {
    key: "todays_calls", label: "Today's Calls", icon: TrendingUp,
    tint: "text-[#4F8CFF]",
    to: "/leads?followup_bucket=today&title=Today%27s+Calls",
    theme: {
      // Light: White → Soft Sky Blue · Dark: deep navy → indigo tint
      bg: "bg-[linear-gradient(135deg,#FFFFFF_0%,#F5F9FF_60%,#EAF2FF_100%)] dark:bg-[linear-gradient(135deg,#0B1220_0%,#0F1B36_60%,#122148_100%)]",
      border: "border-[#DDE6F5] dark:border-[#1E2A4D]",
      hoverBorder: "hover:border-[#4F8CFF]/50 dark:hover:border-[#4F8CFF]/70",
      glow: "bg-[#4F8CFF]/15 dark:bg-[#4F8CFF]/25",
      value: "text-[#111827] dark:text-slate-50",
    },
  },
  {
    key: "pending_calls", label: "Pending Calls", icon: CalendarClock,
    tint: "text-[#F59E0B]",
    to: "/leads?followup_bucket=pending&title=Pending+Calls",
    theme: {
      // Light: White → Soft Peach · Dark: cocoa → amber undertone
      bg: "bg-[linear-gradient(135deg,#FFFFFF_0%,#FFF7EE_60%,#FFEFDA_100%)] dark:bg-[linear-gradient(135deg,#161009_0%,#241608_60%,#331E09_100%)]",
      border: "border-[#F5E4CC] dark:border-[#3B2A15]",
      hoverBorder: "hover:border-[#F59E0B]/55 dark:hover:border-[#F59E0B]/70",
      glow: "bg-[#F59E0B]/15 dark:bg-[#F59E0B]/25",
      value: "text-[#111827] dark:text-slate-50",
    },
  },
  {
    key: "upcoming_calls", label: "Upcoming Calls", icon: CalendarClock,
    tint: "text-[#7C3AED]",
    to: "/leads?followup_bucket=upcoming&title=Upcoming+Calls",
    theme: {
      // Light: White → Light Lavender · Dark: royal purple void
      bg: "bg-[linear-gradient(135deg,#FFFFFF_0%,#F7F3FF_60%,#EEE6FF_100%)] dark:bg-[linear-gradient(135deg,#100A1F_0%,#1B1236_60%,#261950_100%)]",
      border: "border-[#E4DAF5] dark:border-[#2B1F4B]",
      hoverBorder: "hover:border-[#7C3AED]/50 dark:hover:border-[#A78BFA]/70",
      glow: "bg-[#7C3AED]/15 dark:bg-[#A78BFA]/25",
      value: "text-[#111827] dark:text-slate-50",
    },
  },
];
const ROW2_LEFT = {
  key: "total_leads", label: "Total Leads", icon: Users,
  tint: "text-[#4F8CFF]",
  // Total Leads drill-down excludes Lost / Not Interested / Invalid Number
  // (matches backend KPI). Those live under the Lost Leads KPI only.
  to: "/leads?status_not_in=Lost,Not%20Interested,Invalid%20Number,Duplicate&title=Total+Leads",
  theme: {
    // Light: White → Ice Blue · Dark: midnight → arctic navy
    bg: "bg-[linear-gradient(135deg,#FFFFFF_0%,#F3F8FF_60%,#E6F0FE_100%)] dark:bg-[linear-gradient(135deg,#0A1224_0%,#0E1B38_60%,#12234A_100%)]",
    border: "border-[#DBE6F7] dark:border-[#1D2B4E]",
    hoverBorder: "hover:border-[#4F8CFF]/50 dark:hover:border-[#4F8CFF]/70",
    glow: "bg-[#4F8CFF]/15 dark:bg-[#4F8CFF]/25",
    value: "text-[#111827] dark:text-slate-50",
  },
};
const ROW2_BOOKED = {
  key: "booking_done", label: "Booked", icon: Trophy,
  tint: "text-[#D4A017]",
  to: "/leads?status=Booked&title=Booked",
  theme: {
    // Light: White → Warm Gold · Dark: burnished bronze
    bg: "bg-[linear-gradient(135deg,#FFFFFF_0%,#FFF9EC_55%,#FFEFC7_100%)] dark:bg-[linear-gradient(135deg,#141008_0%,#241C0A_55%,#33270C_100%)]",
    border: "border-[#F1E1B2] dark:border-[#3B2E12]",
    hoverBorder: "hover:border-[#D4A017]/60 dark:hover:border-[#D4A017]/80",
    glow: "bg-[#D4A017]/18 dark:bg-[#D4A017]/28",
    value: "text-[#111827] dark:text-slate-50",
  },
};

const TREND_METRICS = [
  { key: "worked_leads",      label: "Worked Leads",      color: "#2563EB" },
  { key: "todays_calls",      label: "Today's Calls",     color: "#B78E4C" },
  { key: "new_leads",         label: "New Leads",         color: "#0B192C" },
  { key: "total_leads",       label: "Total Leads",       color: "#0B192C" },
  { key: "booking_done",      label: "Booking Done",      color: "#059669" },
  { key: "site_visits_fixed", label: "Site Visits Fixed", color: "#6366F1" },
  { key: "site_visits_done",  label: "Site Visits Done",  color: "#10B981" },
];

const PIE_COLORS = ["#0B192C", "#B78E4C", "#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6"];

export default function Dashboard() {
  const { user, impersonateUser } = useAuth();
  const navigate = useNavigate();
  // While SA/Admin is impersonating, every dashboard read is scoped to that
  // user's ID via ?executive_id=<uid> — backend mirrors what the target user
  // would see. Team/leaderboard sections show under the impersonated role.
  const asUserParam = impersonateUser ? `?executive_id=${impersonateUser.id}` : "";
  const asUserAmp = impersonateUser ? `&executive_id=${impersonateUser.id}` : "";
  const effectiveRole = impersonateUser?.role || user?.role;
  const canSeeSources = user && ["super_admin", "admin"].includes(user.role);
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activities, setActivities] = useState([]);
  const [ts14, setTs14] = useState([]);       // trend 14d
  const [tsMonth, setTsMonth] = useState([]); // monthly calendar
  const [trendMetric, setTrendMetric] = useState("worked_leads");
  const [calMetric, setCalMetric] = useState("worked_leads");
  const [target, setTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, a, lb, t14, tm, tgt] = await Promise.all([
          api.get(`/dashboard/stats${asUserParam}`),
          api.get("/activities/recent?limit=12"),
          api.get("/dashboard/leaderboard"),
          api.get(`/dashboard/timeseries?range=14d${asUserAmp}`),
          api.get(`/dashboard/timeseries?range=month${asUserAmp}`),
          api.get("/my-target"),
        ]);
        setStats(s.data);
        setActivities(a.data.items);
        setLeaderboard(lb.data.rows);
        setTs14(t14.data.days);
        setTsMonth(tm.data.days);
        setTarget(tgt.data);
        if (!impersonateUser && ["admin", "super_admin", "team_leader"].includes(user?.role) && can(user, "team_performance") && user?.team_view_active !== false) {
          const tp = await api.get("/dashboard/team-performance");
          setTeam(tp.data.rows);
        }
      } finally {
        setLoading(false);
      }
    };
    load();

    // Keep KPI numbers ticking without a page refresh — refresh /dashboard/stats
    // every 60s and any time the tab regains focus. This ensures the 60-min
    // grace period auto-flip (Today's Calls → Pending Calls) is reflected on
    // the cards without user intervention.
    let intId;
    const refreshStats = async () => {
      try {
        const s = await api.get(`/dashboard/stats${asUserParam}`);
        setStats(s.data);
      } catch { /* silent */ }
    };
    intId = setInterval(refreshStats, 60_000);
    const onVis = () => { if (document.visibilityState === "visible") refreshStats(); };
    window.addEventListener("focus", refreshStats);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(intId);
      window.removeEventListener("focus", refreshStats);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, impersonateUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-28 rounded-xl bg-secondary/40 animate-pulse" />
        ))}
      </div>
    );
  }

  const trendMetricObj = TREND_METRICS.find((m) => m.key === trendMetric) || TREND_METRICS[0];
  const trendData = ts14.map((d) => ({ date: d.date, value: d[trendMetric] || 0 }));
  const calMetricObj = TREND_METRICS.find((m) => m.key === calMetric) || TREND_METRICS[0];
  const sourceData = Object.entries(stats.source_distribution).map(([name, value]) => ({ name, value }));
  const statusData = Object.entries(stats.status_distribution).map(([name, count]) => ({ name, count }));

  return (
    <div className="space-y-4 -mt-4 md:-mt-6" data-testid="dashboard-root">
      {/* Brand hero — centered, luxe wordmark above the date/time (compact spacing) */}
      <div className="text-center" data-testid="brand-hero">
        <h1
          className="font-display font-black tracking-tight text-4xl md:text-5xl lg:text-6xl
                     bg-gradient-to-br from-[#8f6c30] via-[#B78E4C] to-[#e6c584]
                     dark:from-[#d1a568] dark:via-[#e6c584] dark:to-[#f5e2b1]
                     bg-clip-text text-transparent
                     drop-shadow-[0_2px_12px_rgba(183,142,76,0.25)] dark:drop-shadow-[0_2px_16px_rgba(230,197,132,0.35)] leading-none"
          data-testid="brand-title"
        >
          Get Best Property
        </h1>
      </div>

      {/* Header — greeting sits right below the wordmark (no extra gap) */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs md:text-sm text-muted-foreground uppercase tracking-[0.22em] mb-1.5" data-testid="dashboard-date">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kolkata" })}
            {" · "}
            <span className="font-mono" data-testid="dashboard-time">
              {new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}
              <span className="text-[10px] text-muted-foreground/70 ml-1">IST</span>
            </span>
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight" data-testid="dashboard-greeting">
            Good {(() => {
              const h = Number(new Date().toLocaleString("en-GB", { hour: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }));
              return h < 12 ? "Morning" : h < 17 ? "Afternoon" : "Evening";
            })()},{" "}
            <span className="text-[#B78E4C]">{user?.name?.split(" ")[0] || "there"}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="dashboard-subgreet">
            Here{"\u2019"}s what{"\u2019"}s happening across your pipeline today.
          </p>
        </div>
      </div>

      {/* Daily Target Widget */}
      {target && (
        <TargetWidget
          target={target}
          canEdit={can(user, "edit_daily_target")}
          editing={editTarget}
          onToggleEdit={() => setEditTarget(!editTarget)}
          onSave={async (payload) => {
            try {
              const r = await api.put("/my-target", payload);
              setTarget((t) => ({ ...t, ...r.data }));
              setEditTarget(false);
              toast.success("Daily target updated");
            } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update target"); }
          }}
        />
      )}

      {/* Lead Pool banner — pre-qualification stage sitting directly above the KPI rows */}
      {user?.is_team_lead && !impersonateUser && user?.team_view_active !== false && <TeamScorecard />}
      <LeadPoolBanner />

      {/* Worked Leads — full-width hero card on top */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <WorkedLeadsSplitCard
          pool={stats.kpis.worked_leads_pool ?? 0}
          followup={stats.kpis.worked_leads_followup ?? 0}
          conversion={stats.kpis.lead_conversions ?? 0}
        />
      </div>

      {/* Remaining KPIs — 2-column grid at every viewport per user spec. Order:
            Row 1: Today's Calls · New Leads
            Row 2: Pending Calls · Upcoming Calls
            Row 3: Total Leads   · Booked
            Row 4: Site Visits (full-width, sub-stats inline) */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <KpiCard k={ROW1_KPIS[0]} val={stats.kpis[ROW1_KPIS[0].key] ?? 0} />
        <NewLeadsSpotlightCard val={stats.kpis.new_leads ?? 0} />
        <KpiCard k={ROW1_KPIS[1]} val={stats.kpis[ROW1_KPIS[1].key] ?? 0} />
        <KpiCard k={ROW1_KPIS[2]} val={stats.kpis[ROW1_KPIS[2].key] ?? 0} />
        <KpiCard k={ROW2_LEFT} val={stats.kpis[ROW2_LEFT.key] ?? 0} />
        <KpiCard k={ROW2_BOOKED} val={stats.kpis[ROW2_BOOKED.key] ?? 0} />
        <div className="col-span-2">
          <SiteVisitsQuadCard
            pending={stats.kpis.sv_pending ?? 0}
            fixed={stats.kpis.site_visits_fixed ?? 0}
            done={stats.kpis.site_visits_done ?? 0}
            upcoming={stats.kpis.sv_upcoming ?? 0}
            fullWidth
          />
        </div>
      </div>

      {/* Trend chart (metric selector) + optional pie */}
      <div className={`grid grid-cols-1 ${canSeeSources ? "lg:grid-cols-12" : ""} gap-4`}>
        <Card className={`${canSeeSources ? "lg:col-span-8" : ""} p-5`} data-testid="trend-card">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-display font-bold text-lg">Trend over last 14 days</h3>
              <p className="text-xs text-muted-foreground">Switch the metric to see any KPI over time</p>
            </div>
            <Select value={trendMetric} onValueChange={setTrendMetric}>
              <SelectTrigger className="w-[220px] h-9" data-testid="trend-metric-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TREND_METRICS.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="metricGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={trendMetricObj.color} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={trendMetricObj.color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8, fontSize: 12,
                  }}
                  formatter={(v) => [v, trendMetricObj.label]}
                />
                <Area type="monotone" dataKey="value" name={trendMetricObj.label} stroke={trendMetricObj.color} strokeWidth={2} fill="url(#metricGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {canSeeSources && (
          <Card className="lg:col-span-4 p-5" data-testid="lead-sources-card">
            <div className="mb-4">
              <h3 className="font-display font-bold text-lg">Lead sources</h3>
              <p className="text-xs text-muted-foreground">Where your leads come from</p>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sourceData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={80}
                    paddingAngle={2}
                    onClick={(d) => {
                      if (d?.name && d.name !== "Unknown")
                        navigate(`/leads?source=${encodeURIComponent(d.name)}`);
                    }}
                    className="cursor-pointer"
                  >
                    {sourceData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="hsl(var(--card))" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8, fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>

      {/* Monthly Calendar with metric */}
      <MonthlyCalendar
        days={tsMonth}
        metric={calMetric}
        metricObj={calMetricObj}
        onMetricChange={setCalMetric}
      />

      {/* Status distribution + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-8 p-5">
          <div className="mb-4">
            <h3 className="font-display font-bold text-lg">Status distribution</h3>
            <p className="text-xs text-muted-foreground">Leads across the 15-status pipeline</p>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={statusData}
                margin={{ top: 10, right: 10, bottom: 40, left: 0 }}
                onClick={(e) => {
                  const name = e?.activePayload?.[0]?.payload?.name;
                  if (name) navigate(`/leads?status=${encodeURIComponent(name)}`);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10}
                       angle={-35} textAnchor="end" interval={0} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "hsl(var(--secondary))", opacity: 0.5 }}
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8, fontSize: 12,
                  }}
                />
                <Bar dataKey="count" fill="#0B192C" radius={[4, 4, 0, 0]} className="cursor-pointer" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="lg:col-span-4 p-5">
          <div className="mb-4">
            <h3 className="font-display font-bold text-lg">Recent activity</h3>
            <p className="text-xs text-muted-foreground">Latest calls & new leads</p>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {activities.length === 0 && (
              <div className="text-sm text-muted-foreground">No recent activity.</div>
            )}
            {activities.map((a, i) => (
              <div
                key={i}
                data-testid={`activity-${i}`}
                onClick={() => {
                  if (a.lead?.lead_code) {
                    navigate(`/leads?q=${encodeURIComponent(a.lead.lead_code)}`);
                  }
                }}
                className="flex items-start gap-3 text-sm cursor-pointer rounded-md -mx-1 px-1 py-1 hover:bg-secondary/60 transition-colors"
              >
                <div className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${a.type === "followup" ? "bg-[#B78E4C]" : "bg-emerald-500"}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium truncate">{a.lead?.customer_name || "Unknown"}</span>
                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">{a.lead?.lead_code}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {a.type === "followup" ? (
                      <>Follow-up · <span className="font-medium">{a.outcome}</span> by {a.executive}</>
                    ) : (
                      <>New lead assigned to {a.executive || "—"}</>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                    {(() => { try { return formatDistanceToNow(parseISO(a.at), { addSuffix: true }); } catch { return ""; } })()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Leaderboard — hidden for tele_sales users; leaders/admins still see it */}
      {leaderboard.length > 0 && user?.role !== "tele_sales" && (
        <Card className="p-5 overflow-hidden" data-testid="leaderboard">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-[#B78E4C]" />
                Executive Leaderboard
              </h3>
              <p className="text-xs text-muted-foreground">Today&#39;s calls · bookings · conversion. Ranked in real time.</p>
            </div>
            <span className="text-[11px] text-muted-foreground uppercase tracking-widest">
              {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium w-12">Rank</th>
                  <th className="py-2 pr-4 font-medium">Executive</th>
                  <th className="py-2 pr-4 font-medium text-right">Calls Today</th>
                  <th className="py-2 pr-4 font-medium text-right">Site Visits</th>
                  <th className="py-2 pr-4 font-medium text-right">Bookings</th>
                  <th className="py-2 font-medium">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((r) => {
                  const isMe = r.executive_id === user?.id;
                  const RankIcon = r.rank === 1 ? Crown : (r.rank === 2 || r.rank === 3) ? Medal : null;
                  const rankColor = r.rank === 1 ? "text-[#B78E4C]" : r.rank === 2 ? "text-slate-400" : r.rank === 3 ? "text-amber-700" : "text-muted-foreground";
                  return (
                    <tr
                      key={r.executive_id}
                      data-testid={`leaderboard-row-${r.executive}`}
                      onClick={() => navigate(`/leads?q=${encodeURIComponent(r.executive)}`)}
                      className={`border-b border-border/50 hover:bg-secondary/40 cursor-pointer ${isMe ? "bg-[#B78E4C]/5" : ""}`}
                    >
                      <td className="py-3 pr-4">
                        <span className={`inline-flex items-center gap-1.5 font-mono font-bold ${rankColor}`}>
                          {RankIcon && <RankIcon className="h-4 w-4" />}
                          #{r.rank}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="font-medium">
                          {r.executive}
                          {isMe && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#B78E4C] font-semibold">You</span>}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <span className="inline-flex items-center gap-1 font-mono text-base">
                          {r.calls_today > 0 && <PhoneCall className="h-3 w-3 text-[#B78E4C]" />}
                          {r.calls_today}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right font-mono">{r.site_visits}</td>
                      <td className="py-3 pr-4 text-right font-mono font-semibold">
                        {r.bookings > 0 ? (
                          <span className="text-emerald-600 dark:text-emerald-400">{r.bookings}</span>
                        ) : r.bookings}
                      </td>
                      <td className="py-3">
                        <span className="inline-flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#B78E4C] to-emerald-500" style={{ width: `${Math.min(r.conversion, 100)}%` }} />
                          </div>
                          <span className="font-mono text-xs">{r.conversion}%</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- Sub-components ----------
function WorkedLeadsSplitCard({ pool, followup, conversion }) {
  const total = (pool || 0) + (followup || 0);
  const poolPct  = total ? Math.round((pool / total) * 100)  : 0;
  const followupPct = total ? 100 - poolPct : 0;
  return (
    // Gradient border wrapper — soft lavender → purple → warm gold. The p-[1.5px]
    // creates the visible border strip; outer drop-shadow gives the "gentle pop".
    <div
      className="relative p-[1.5px] rounded-2xl bg-gradient-to-br from-[#C7B8FF] via-[#B78E4C] to-[#FFC49B] shadow-[0_10px_30px_-12px_rgba(183,142,76,0.35),0_4px_14px_-4px_rgba(199,184,255,0.35)]"
      data-testid="kpi-worked_leads"
    >
      {/* Outer glow — sits behind the card so it feels lifted */}
      <div className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-br from-[#C7B8FF]/25 via-transparent to-[#FFC49B]/25 blur-2xl -z-10" />

      <div className="relative overflow-hidden rounded-[calc(1rem-1.5px)] p-4 sm:p-5 h-full
                      bg-[linear-gradient(135deg,#FFFFFF_0%,#F7F3FF_38%,#FFF7F0_72%,#FFFCF7_100%)]
                      dark:bg-[linear-gradient(135deg,#0E0820_0%,#1A0F38_38%,#2A1030_72%,#301826_100%)]">
        {/* Ambient soft mesh — 3-6% opacity abstract shapes */}
        <div className="pointer-events-none absolute -top-16 -right-10 w-44 h-44 rounded-full bg-[#B78E4C]/[0.05] dark:bg-[#B78E4C]/[0.18] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 w-44 h-44 rounded-full bg-[#C7B8FF]/[0.05] dark:bg-[#8B5CF6]/[0.22] blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 left-1/3 w-32 h-32 rounded-full bg-[#FFC49B]/[0.04] dark:bg-[#F97316]/[0.14] blur-2xl" />
        {/* Dotted micro-pattern (extremely subtle) */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #A78BFA 1px, transparent 0)",
            backgroundSize: "18px 18px",
          }}
        />
        {/* Wave curve */}
        <svg
          className="pointer-events-none absolute -bottom-2 -right-2 w-40 h-24 opacity-[0.06]"
          viewBox="0 0 200 100"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M0 60 Q 50 20 100 55 T 200 45"
            stroke="url(#wl-wave)"
            strokeWidth="2"
            fill="none"
          />
          <defs>
            <linearGradient id="wl-wave" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="60%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#F97316" />
            </linearGradient>
          </defs>
        </svg>

        {/* Header: label + glowing gradient icon badge */}
        <div className="relative flex items-start justify-between mb-4">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700 dark:text-slate-200">
            Worked Leads
          </span>
          <div className="relative">
            {/* Outer glow behind the badge */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#8B5CF6] via-[#EC4899] to-[#F97316] blur-lg opacity-50 dark:opacity-70" />
            {/* Glass badge with gradient fill */}
            <div className="relative h-9 w-9 rounded-full bg-gradient-to-br from-[#8B5CF6] via-[#EC4899] to-[#F97316] flex items-center justify-center shadow-[0_4px_14px_-2px_rgba(236,72,153,0.45)] ring-1 ring-white/60 dark:ring-white/25">
              <PhoneCall className="h-4 w-4 text-white drop-shadow" />
            </div>
          </div>
        </div>

        {/* Three stat boxes — Pool · Follow Up · Conversion */}
        <div className="relative grid grid-cols-3 gap-2 sm:gap-3">
          {/* Pool — non-clickable */}
          <div
            data-testid="kpi-worked_leads_pool"
            className="rounded-xl border border-white/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.04] backdrop-blur-md p-3 select-none cursor-default shadow-[0_2px_10px_-2px_rgba(139,92,246,0.12)]"
            title="Pool status updates you made today (dropdown clicks)"
          >
            <div className="text-[9px] uppercase tracking-widest text-slate-600 dark:text-slate-300 font-semibold">Pool</div>
            <div
              data-testid="kpi-worked_leads_pool-val"
              className="font-display font-bold text-2xl md:text-3xl leading-none mt-1 bg-gradient-to-br from-[#7C3AED] to-[#A78BFA] bg-clip-text text-transparent"
            >
              {Number(pool || 0).toLocaleString("en-IN")}
            </div>
          </div>

          {/* Follow Up — clickable, orange glowing dot */}
          <Link
            to="/leads?worked_today=true&title=Worked+Leads+%C2%B7+Today"
            data-testid="kpi-worked_leads_followup"
            className="group relative rounded-xl border border-white/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.04] backdrop-blur-md p-3 hover:border-[#F97316]/50 hover:bg-white/75 dark:hover:bg-white/[0.08] transition shadow-[0_2px_10px_-2px_rgba(249,115,22,0.14)]"
          >
            <div className="flex items-center gap-1.5">
              <div className="text-[9px] uppercase tracking-widest text-slate-600 dark:text-slate-300 font-semibold">Follow Up</div>
              {/* Small glowing orange notification dot — always visible per spec */}
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F97316] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#F97316] shadow-[0_0_6px_rgba(249,115,22,0.7)]" />
              </span>
            </div>
            <div
              data-testid="kpi-worked_leads_followup-val"
              className="font-display font-bold text-2xl md:text-3xl leading-none mt-1 bg-gradient-to-br from-[#F97316] to-[#FB923C] bg-clip-text text-transparent"
            >
              {Number(followup || 0).toLocaleString("en-IN")}
            </div>
          </Link>

          {/* Conversion — clickable, emerald glowing dot. Counts DISTINCT
              leads this user moved from "New" to "Follow Up" or "Requirement"
              TODAY. "Call Back" is intentionally excluded (per spec). */}
          <Link
            to="/leads?conversions_today=true&title=Conversions+%C2%B7+Today"
            data-testid="kpi-worked_leads_conversion"
            className="group relative rounded-xl border border-white/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.04] backdrop-blur-md p-3 hover:border-emerald-500/50 hover:bg-white/75 dark:hover:bg-white/[0.08] transition shadow-[0_2px_10px_-2px_rgba(16,185,129,0.14)]"
            title="New Leads moved to Follow-up or Requirement today"
          >
            <div className="flex items-center gap-1.5">
              <div className="text-[9px] uppercase tracking-widest text-slate-600 dark:text-slate-300 font-semibold">Conversion</div>
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)]" />
              </span>
            </div>
            <div
              data-testid="kpi-worked_leads_conversion-val"
              className="font-display font-bold text-2xl md:text-3xl leading-none mt-1 bg-gradient-to-br from-emerald-600 to-emerald-400 bg-clip-text text-transparent"
            >
              {Number(conversion || 0).toLocaleString("en-IN")}
            </div>
          </Link>
        </div>

        {/* Premium gradient progress bar — Purple → Pink → Orange with glow beneath */}
        <div className="relative mt-4">
          {/* Glow beneath */}
          <div className="absolute -inset-x-1 -bottom-1 h-2 rounded-full bg-gradient-to-r from-[#8B5CF6]/40 via-[#EC4899]/40 to-[#F97316]/40 blur-md" />
          <div
            className="relative h-1.5 w-full rounded-full bg-slate-200/70 dark:bg-white/[0.08] overflow-hidden"
            data-testid="kpi-worked_leads_ratio"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={poolPct}
            aria-label={`Pool ${poolPct}% · Follow-Up ${followupPct}%`}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F97316] transition-all duration-500"
              style={{ width: `${Math.max(poolPct + followupPct === 0 ? 0 : Math.min(100, poolPct + followupPct), 0)}%` }}
            />
          </div>
        </div>

        {/* Footer — Today · Live  |  Total pill */}
        <div className="relative mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-600 dark:text-slate-300 font-semibold">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.7)]" />
            </span>
            <span>Today · Live</span>
          </div>
          <div className="text-[10px] font-mono font-semibold tracking-wider text-slate-700 dark:text-slate-100 bg-white/70 dark:bg-white/[0.06] border border-white/80 dark:border-white/10 backdrop-blur-md rounded-full px-2.5 py-0.5 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.12)]">
            Total {total.toLocaleString("en-IN")}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ k, val }) {
  const Icon = k.icon;
  const t = k.theme || {};
  return (
    <Link to={k.to} data-testid={`kpi-${k.key}`} className="block group">
      <Card
        className={`relative overflow-hidden p-5 h-full ${t.bg || ""} ${t.border || "border-[#E8ECF4]"} shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_16px_-6px_rgba(15,23,42,0.06)] hover:-translate-y-[3px] hover:shadow-[0_10px_28px_-10px_rgba(15,23,42,0.14)] ${t.hoverBorder || "hover:border-[#B78E4C]/60"} transition-[transform,box-shadow,border-color] duration-200 cursor-pointer`}
      >
        {/* Soft radial glow behind the icon corner */}
        <div className={`pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full blur-3xl ${t.glow || "bg-[#B78E4C]/10"}`} />
        <div className="relative flex items-start justify-between mb-4">
          <span className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.14em] group-hover:text-[#111827] dark:group-hover:text-slate-100 transition-colors">
            {k.label}
          </span>
          <Icon className={`h-5 w-5 ${k.tint}`} />
        </div>
        <div className={`relative font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-none break-anywhere ${t.value || "text-[#111827] dark:text-slate-50"}`}>
          {Number(val).toLocaleString("en-IN")}
        </div>
        <div className="relative mt-2 text-[10px] text-[#6B7280]/80 dark:text-slate-400/70 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
          View details →
        </div>
      </Card>
    </Link>
  );
}

function SiteVisitsQuadCard({ pending, fixed, done, upcoming, fullWidth = false }) {
  const total = pending + fixed + done + upcoming;
  return (
    <Card
      className="relative overflow-hidden p-5 h-full border-[#E4DAF5] dark:border-[#2B1F4B] bg-[linear-gradient(135deg,#FFFFFF_0%,#F8F5FF_60%,#F0EAFE_100%)] dark:bg-[linear-gradient(135deg,#100A1F_0%,#1B1236_60%,#261950_100%)] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_16px_-6px_rgba(15,23,42,0.06)]"
      data-testid="kpi-site_visits"
    >
      <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-[#7C3AED]/12 dark:bg-[#A78BFA]/25 blur-3xl" />
      <div className="relative flex items-start justify-between mb-3">
        <span className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.14em]">Site Visits</span>
        <Home className="h-5 w-5 text-[#7C3AED] dark:text-[#A78BFA]" />
      </div>
      <div className={`relative grid ${fullWidth ? "grid-cols-4" : "grid-cols-2"} gap-2`}>
        <SvQuadCell
          to="/leads?status=Site%20Visit%20Fixed&overdue=true&title=Site+Visits+%C2%B7+Pending"
          testId="kpi-sv_pending"
          label="Pending"
          val={pending}
          tint="text-[#F59E0B]"
          hoverBorder="hover:border-[#F59E0B]/60"
        />
        <SvQuadCell
          to="/leads?status=Site%20Visit%20Fixed&title=Site+Visits+%C2%B7+Fixed"
          testId="kpi-site_visits_fixed"
          label="Fixed"
          val={fixed}
          tint="text-[#7C3AED]"
          hoverBorder="hover:border-[#7C3AED]/50"
        />
        <SvQuadCell
          to="/leads?status=Site%20Visit%20Done&title=Site+Visits+%C2%B7+Done"
          testId="kpi-site_visits_done"
          label="Done"
          val={done}
          tint="text-[#10B981]"
          hoverBorder="hover:border-[#10B981]/50"
        />
        <SvQuadCell
          to="/leads?status=Site%20Visit%20Fixed&upcoming=true&title=Site+Visits+%C2%B7+Upcoming"
          testId="kpi-sv_upcoming"
          label="Upcoming"
          val={upcoming}
          tint="text-[#4F8CFF]"
          hoverBorder="hover:border-[#4F8CFF]/50"
        />
      </div>
      {total > 0 && (
        <div className="relative mt-3 text-[10px] uppercase tracking-widest text-[#6B7280] dark:text-slate-400 text-center">
          Total: <span className="font-mono font-semibold text-sm text-[#111827] dark:text-slate-100">{total}</span>
        </div>
      )}
    </Card>
  );
}

function SvQuadCell({ to, testId, label, val, tint, hoverBorder }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`rounded-md border border-[#E8ECF4] dark:border-[#22193E] bg-white/70 dark:bg-slate-950/40 backdrop-blur-sm px-2.5 py-2 ${hoverBorder} hover:bg-white dark:hover:bg-slate-900/60 transition-colors block`}
    >
      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] dark:text-slate-400 leading-tight">{label}</div>
      <div className={`font-display font-bold text-2xl md:text-3xl tracking-tight ${tint}`}>
        {Number(val).toLocaleString("en-IN")}
      </div>
    </Link>
  );
}

function NewLeadsSpotlightCard({ val }) {
  return (
    <Link to="/leads?status=New&title=New+Leads" data-testid="kpi-new_leads" className="block group">
      <Card className="relative p-5 h-full overflow-hidden cursor-pointer border-[#F1E1B2] dark:border-[#3B2E12] bg-[linear-gradient(135deg,#FFFFFF_0%,#FFFAEC_55%,#FFF0CC_100%)] dark:bg-[linear-gradient(135deg,#141008_0%,#241C0A_55%,#33270C_100%)] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_16px_-6px_rgba(212,160,23,0.14)] hover:-translate-y-[3px] hover:shadow-[0_10px_28px_-10px_rgba(212,160,23,0.28)] hover:border-[#D4A017] transition-[transform,box-shadow,border-color] duration-200">
        <div className="absolute -right-6 -top-6 h-28 w-28 rounded-full bg-[#D4A017]/18 dark:bg-[#D4A017]/28 blur-2xl pointer-events-none" />
        <div className="relative flex items-start justify-between mb-4">
          <span className="text-xs font-semibold text-[#B78E4C] dark:text-[#D4A017] uppercase tracking-[0.16em]">
            New Leads
          </span>
          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#D4A017] to-[#B78E4C] flex items-center justify-center shadow-[0_4px_10px_-2px_rgba(212,160,23,0.4)] ring-1 ring-white/60 dark:ring-white/20">
            <Users className="h-4 w-4 text-white" />
          </div>
        </div>
        <div className="relative flex items-baseline gap-2">
          <span className="font-display font-bold text-4xl md:text-5xl tracking-tight leading-none text-[#111827] dark:text-slate-50">
            {Number(val).toLocaleString("en-IN")}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#B78E4C] dark:text-[#D4A017] uppercase tracking-widest">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4A017] animate-pulse shadow-[0_0_6px_rgba(212,160,23,0.7)]" />
            fresh
          </span>
        </div>
        <div className="relative mt-2 text-[10px] text-[#6B7280] dark:text-slate-400 uppercase tracking-widest group-hover:text-[#B78E4C] dark:group-hover:text-[#D4A017] transition-colors">
          Work these first →
        </div>
      </Card>
    </Link>
  );
}

function TargetWidget({ target, canEdit, editing, onToggleEdit, onSave }) {
  const [callsInput, setCallsInput] = useState(target.calls_target);
  const [convInput, setConvInput] = useState(target.conversions_target);
  const [svfInput, setSvfInput] = useState(target.sv_fixed_target);
  const [svdInput, setSvdInput] = useState(target.sv_done_target);
  useEffect(() => {
    setCallsInput(target.calls_target);
    setConvInput(target.conversions_target);
    setSvfInput(target.sv_fixed_target);
    setSvdInput(target.sv_done_target);
  }, [target]);

  const pct = (done, goal) => goal > 0 ? Math.min(100, Math.round((done / goal) * 100)) : 0;

  return (
    <Card
      className="relative overflow-hidden p-4 md:p-5 border-[#F1E1B2] dark:border-[#3B2E12] bg-[linear-gradient(135deg,#FFFFFF_0%,#FFFBEE_55%,#FFF3D1_100%)] dark:bg-[linear-gradient(135deg,#141008_0%,#241C0A_55%,#33270C_100%)] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_6px_20px_-8px_rgba(212,160,23,0.14)]"
      data-testid="daily-target"
    >
      <div className="pointer-events-none absolute -top-16 -left-10 h-40 w-40 rounded-full bg-[#D4A017]/[0.08] dark:bg-[#D4A017]/[0.18] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -right-10 h-40 w-40 rounded-full bg-[#B78E4C]/[0.07] dark:bg-[#B78E4C]/[0.16] blur-3xl" />
      {/* Centered title */}
      <div className="relative flex items-center justify-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-md bg-gradient-to-br from-[#D4A017] to-[#B78E4C] flex items-center justify-center shadow-[0_4px_10px_-2px_rgba(212,160,23,0.35)] ring-1 ring-white/60 dark:ring-white/20">
          <Target className="h-4 w-4 text-white" />
        </div>
        <h3 className="font-display font-bold text-lg md:text-xl leading-none text-[#111827] dark:text-slate-50">Today{"\u2019"}s Target</h3>
        {canEdit && (
          !editing ? (
            <Button variant="ghost" size="sm" onClick={onToggleEdit} data-testid="edit-target" className="text-[10px] h-6 px-2 ml-2">
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          ) : (
            <Button size="sm" onClick={() => onSave({
              calls_target: Number(callsInput),
              conversions_target: Number(convInput),
              sv_fixed_target: Number(svfInput),
              sv_done_target: Number(svdInput),
            })} data-testid="target-save" className="h-7 bg-[#0B192C] hover:bg-[#0B192C]/90 text-white ml-2 px-2 text-[10px]">
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
          )
        )}
      </div>

      {/* Metric row — Calls / Conversion / SV Fixed / SV Done, all inline, all identical style.
          Responsive: 2 cols on mobile, 4 cols on tablet+. */}
      <div className="relative space-y-3 md:space-y-4" data-testid="target-metrics">
        <TargetLine big label="Conversion" done={target.conversions_done} goal={target.conversions_target} pct={pct(target.conversions_done, target.conversions_target)} tint="text-[#10B981]" testId="target-conversion" />
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          <TargetLine label="Calls"    done={target.calls_done}    goal={target.calls_target}         pct={pct(target.calls_done, target.calls_target)}       tint="text-[#D4A017]" testId="target-calls" />
          <TargetLine label="SV Fixed" done={target.sv_fixed_done} goal={target.sv_fixed_target || 0} pct={pct(target.sv_fixed_done, target.sv_fixed_target)} tint="text-[#7C3AED]" testId="target-sv-fixed" />
        </div>
      </div>

      {editing && canEdit && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-md bg-secondary/40 border border-border">
          <TargetInput label="Calls target" value={callsInput} onChange={setCallsInput} testid="target-calls-input" />
          <TargetInput label="Conversions target" value={convInput} onChange={setConvInput} testid="target-conv-input" />
        </div>
      )}
    </Card>
  );
}

/**
 * Attractive box tile matching the requested layout — each metric sits inside its own bordered card:
 *   ┌───────────────────────┐
 *   │  Calls - 1/250        │
 *   │  0%                   │
 *   └───────────────────────┘
 */
function TargetLine({ label, done, goal, pct, tint, big = false, testId }) {
  const hit = goal > 0 && pct >= 100;
  return (
    <div data-testid={testId} className={`${big ? "py-4" : ""} rounded-lg border-2 ${hit ? "border-[#10B981]/50 bg-[#ECFDF5] dark:border-[#10B981]/60 dark:bg-[#052E20]/60" : "border-[#F1E1B2] dark:border-[#3B2E12] bg-white/70 dark:bg-slate-950/40 backdrop-blur-sm"} px-3 py-2.5 text-center shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]`}>
      <div className={`font-display font-bold ${big ? "text-xl md:text-2xl" : "text-base md:text-lg"} ${tint}`}>
        {label} <span className="text-[#111827] dark:text-slate-100">- {done}/{goal || 0}</span>
      </div>
      <div className={`text-xs md:text-sm font-mono mt-0.5 ${hit ? "text-[#059669] dark:text-[#34D399] font-semibold" : "text-[#6B7280] dark:text-slate-400"}`}>
        {pct}%{hit && " · Target hit"}
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-2 mx-auto max-w-md">
        <div className={`h-full ${hit ? "bg-[#10B981]" : "bg-[#B78E4C]"} transition-[width] duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

function TargetInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="h-9 mt-1" data-testid={testid} />
    </div>
  );
}

function TargetProgress({ label, done, goal, pct, color }) {
  const hit = pct >= 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</span>
        {hit && <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Target hit!</span>}
      </div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="font-display font-bold text-3xl">{done}</span>
        <span className="text-sm text-muted-foreground">/ {goal}</span>
        <span className="ml-auto text-xs font-mono text-muted-foreground">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${color} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ---------- Monthly Calendar ----------
function MonthlyCalendar({ days, metric, metricObj, onMetricChange }) {
  const navigate = useNavigate();
  // Build a month grid starting from Sunday
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const startWeekday = firstDay.getDay(); // 0=Sun
  const monthLabel = now.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const today = now.getDate();

  // Map iso→count
  const byDay = {};
  days.forEach((d) => { byDay[d.day] = d[metric] || 0; });

  const totalCells = startWeekday + days.length;
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  days.forEach((d) => cells.push(d));
  // pad to full weeks
  while (cells.length % 7 !== 0) cells.push(null);

  const maxVal = Math.max(1, ...days.map((d) => d[metric] || 0));

  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <Card className="p-5" data-testid="monthly-calendar">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Layers className="h-4 w-4 text-[#B78E4C]" />
            {monthLabel} · <span style={{ color: metricObj.color }}>{metricObj.label}</span>
          </h3>
          <p className="text-xs text-muted-foreground">Pick a metric and see how each day of this month performed</p>
        </div>
        <Select value={metric} onValueChange={onMetricChange}>
          <SelectTrigger className="w-[220px] h-9" data-testid="calendar-metric-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TREND_METRICS.map((m) => (
              <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        {weekdayLabels.map((w) => (
          <div key={w} className="text-center py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="h-16" />;
          const v = c[metric] || 0;
          const intensity = v > 0 ? Math.max(0.15, Math.min(1, v / maxVal)) : 0;
          const isToday = c.day === today;
          return (
            <button
              key={i}
              onClick={() => v > 0 && navigate(`/leads?q=${encodeURIComponent(c.iso)}`)}
              disabled={v === 0}
              data-testid={`cal-day-${c.day}`}
              title={`${c.date} — ${metricObj.label}: ${v}`}
              className={`h-16 rounded-md border p-1.5 text-left transition
                ${isToday ? "border-[#B78E4C]" : "border-border"}
                ${v > 0 ? "cursor-pointer hover:border-[#B78E4C]" : "cursor-default"}`}
              style={{
                background: v > 0 ? `${metricObj.color}${Math.round(intensity * 40).toString(16).padStart(2, "0")}` : "transparent",
              }}
            >
              <div className={`text-[11px] font-mono ${isToday ? "font-bold text-[#B78E4C]" : "text-muted-foreground"}`}>
                {c.day}
              </div>
              {v > 0 && (
                <div className="font-display font-bold text-lg leading-tight" style={{ color: metricObj.color }}>
                  {v}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}


/**
 * Lead Pool banner — sits above the KPI rows on the Dashboard. Fetches the pool
 * total on mount so the count is live. Click-through routes to /lead-pool.
 */
function LeadPoolBanner() {
  const { user } = useAuth();
  const isManager = user?.role === "super_admin" || user?.role === "admin";
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isManager) return;
    api.get("/lead-pool/counts")
      .then((r) => setCount(r.data?.total ?? 0))
      .catch(() => {});
  }, [isManager]);
  return (
    <Link to="/lead-pool" className="block group" data-testid="dashboard-lead-pool-banner">
      <Card className="relative overflow-hidden p-5 md:p-6 border-2 border-[#F1E1B2] dark:border-[#3B2E12] bg-[linear-gradient(135deg,#FFFFFF_0%,#FFFBEF_60%,#FFF3D9_100%)] dark:bg-[linear-gradient(135deg,#141008_0%,#241C0A_60%,#33270C_100%)] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_6px_20px_-8px_rgba(212,160,23,0.14)] hover:border-[#D4A017] hover:shadow-[0_10px_28px_-8px_rgba(212,160,23,0.22)] transition-all">
        <div className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-[#D4A017]/[0.08] dark:bg-[#D4A017]/[0.18] blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-[#B78E4C]/[0.06] dark:bg-[#B78E4C]/[0.16] blur-3xl" />
        <div className="relative flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-[#D4A017] to-[#B78E4C] text-white flex items-center justify-center flex-shrink-0 shadow-[0_6px_14px_-4px_rgba(212,160,23,0.45)] ring-1 ring-white/60 dark:ring-white/20 group-hover:scale-105 transition-transform">
            <ClipboardList className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0 flex justify-center">
            <div className="font-display font-bold text-xl md:text-2xl text-[#0B192C] dark:text-slate-50">Lead Pool</div>
          </div>
          {isManager && (
            <div className="flex flex-col items-end gap-1 pr-1">
              <span className="text-[10px] uppercase tracking-widest text-[#6B7280] dark:text-slate-400">In pool</span>
              <span className="font-display font-bold text-3xl md:text-4xl leading-none text-[#0B192C] dark:text-slate-50" data-testid="dashboard-lead-pool-count">
                {count}
              </span>
            </div>
          )}
          <ChevronRight className="h-6 w-6 text-[#D4A017] flex-shrink-0" />
        </div>
      </Card>
    </Link>
  );
}
